import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {IconButton} from 'shells/button/icon-button/icon-button';
import {IconButtonWithConfirmation} from 'shells/button/icon-button/icon-button-with-confirmation';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {FileTextRegularIcon} from 'shells/icon/svg/file-text-regular-icon';
// @ts-expect-error - imported from .js/.jsx file
import {RetryRegularIcon} from 'shells/icon/svg/retry-regular-icon';
import {TrashRegularIcon} from 'shells/icon/svg/trash-regular-icon';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Body} from 'shells/typography/body';

import type {GetActivityForModal_activity as Activity} from 'nutshell-graphql-types';

import {useDeleteTranscript} from '../graphql/hooks';
import {useActivitySummarize} from '../graphql/hooks/use-activity-summarize';
import {useGetActivityTranscriptAndSummary} from '../graphql/hooks/use-get-activity-transcript-and-summary';

import {EmptyTab} from './empty-tab';

type Props = {
    activity: Activity;
    isAdmin?: boolean;
};

export function Transcript(props: Props) {
    const {transcription, transcriptionStatus} = props.activity;

    const [isRetryingTranscription, setIsRetryingTranscription] = React.useState<boolean>(false);

    const {refetch: refetchTranscriptAndSummary} = useGetActivityTranscriptAndSummary(
        props.activity.id,
        true
    );

    const {
        deleteTranscript,
        isLoading: isDeletingTranscript,
        error: deleteTranscriptError,
    } = useDeleteTranscript();

    // using this mutation to get the transcript when it errors. Our transcripts
    // and summaries are tightly coupled to simplify credit logic, and if our
    // transcript errored that means the summary did too. This mutation will try
    // to create the transcript if it doesn't exist/errored, then summarize that
    // transcript after.
    const {activitySummarize} = useActivitySummarize();

    const clearPolling = (interval: number) => {
        setIsRetryingTranscription(false);
        clearInterval(interval);
    };

    const handlePollForTranscription = () => {
        const interval = setInterval(() => {
            refetchTranscriptAndSummary()
                .then((result) => {
                    if (result.data && result.data.activity) {
                        const activity = result.data.activity;
                        const status = activity.transcriptionStatus;

                        if (status === 'NOT_ENABLED' || status === 'ERRORED') {
                            // @ts-expect-error
                            clearPolling(interval);

                            return;
                        }

                        if (activity.transcription && status !== 'ERRORED') {
                            // @ts-expect-error
                            clearPolling(interval);
                        }
                    }
                })
                .catch(() => {
                    // @ts-expect-error
                    clearPolling(interval);
                });
        }, 1500);
    };

    const handleDeleteTranscript = () => {
        deleteTranscript({
            activityId: props.activity.id,
        });
    };

    const handleRetryTranscription = () => {
        setIsRetryingTranscription(true);
        activitySummarize({
            activityId: props.activity.id,
        }).then(() => {
            handlePollForTranscription();
        });
    };

    if (isDeletingTranscript || isRetryingTranscription) {
        return <LoadingPage />;
    }

    if (props.activity.summaryStates.isNotetakerDisabled) {
        return (
            <EmptyTab
                title='No transcript available'
                subtitle={props.activity.summaryStates.transcriptionEmptyState}
                icon={FileTextRegularIcon}
            />
        );
    }

    const summaryAiEnabled = NutClientConfig.enableAi;

    if (
        transcriptionStatus === 'ERRORED' &&
        props.activity.canViewSummaryDetails &&
        summaryAiEnabled
    ) {
        return (
            <div className='flex pad-16 full-width height-fit-content align-center justify-sb'>
                <Body size='small' color='error'>
                    There was an error generating the transcription, please try again.
                </Body>
                <IconButton
                    icon={RetryRegularIcon}
                    tooltipText='Retry transcription'
                    onClick={() => {
                        handleRetryTranscription();
                    }}
                />
            </div>
        );
    }

    if (
        (transcriptionStatus === 'NOT_ENABLED' ||
            (transcriptionStatus === 'ERRORED' && !summaryAiEnabled)) &&
        props.activity.canViewSummaryDetails
    ) {
        let message;
        if (summaryAiEnabled) {
            message =
                'AI transcripts and summaries could not be generated at this time, please try again.';
        } else if (props.isAdmin) {
            message = (
                <>
                    You’ve disabled AI transcripts and summaries for your account.{' '}
                    {/* @ts-expect-error */}
                    <Link as={RoutedLink} to='/setup/ai-features' variant='primary' size='small'>
                        Update settings
                    </Link>
                </>
            );
        } else {
            message = 'AI transcripts and summaries are disabled for your account';
        }

        return (
            <div className='flex pad-16 full-width height-fit-content align-center justify-sb'>
                <Body size='small' color='warn'>
                    {message}
                </Body>
                {summaryAiEnabled && (
                    <IconButton
                        icon={RetryRegularIcon}
                        tooltipText='Retry transcription'
                        onClick={() => {
                            handleRetryTranscription();
                        }}
                    />
                )}
            </div>
        );
    }

    if (transcription && props.activity.canViewSummaryDetails) {
        return (
            <div className='full-width full-height flex flex-dir-col'>
                {deleteTranscriptError && (
                    <div className='mx-8 mt-16'>
                        <FlashBanner type='error' hasBorder={true} noRightPadding={true}>
                            Error removing transcript, please refresh and try again, or contact
                            support.
                        </FlashBanner>
                    </div>
                )}
                <div className='full-width full-height overflow-auto'>
                    <div className='pad-16 flex justify-end pos-sticky top-0 background-white'>
                        <IconButtonWithConfirmation
                            disabled={Boolean(isDeletingTranscript || deleteTranscriptError)}
                            onConfirm={handleDeleteTranscript}
                            icon={TrashRegularIcon}
                            iconSize={12}
                            tooltipText='Remove transcript'
                            question='Are you sure you want to remove this transcript from the activity?'
                            dialogWidth='240px'
                            noHoverBackground={true}
                            noPadding={true}
                            iconHoverWhenConfirming={true}
                        />
                    </div>
                    <div className='px-16 pb-16'>
                        <Body size='small' maintainLineBreaks={true} as='div'>
                            {transcription}
                        </Body>
                    </div>
                </div>
            </div>
        );
    }

    const subtitle = props.activity.summaryStates.transcriptionEmptyState;

    return (
        <EmptyTab title='No transcript available' subtitle={subtitle} icon={FileTextRegularIcon} />
    );
}
